import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  FaTasks, FaComments, FaFile, FaUsers, FaChartLine, FaPlus,
  FaTimes, FaPaperclip, FaTrash, FaEdit, FaCheck, FaBell,
  FaCalendarAlt, FaLink, FaExclamationCircle, FaCheckCircle,
  FaClock, FaSearch, FaEllipsisV, FaPaperPlane, FaSmile,
  FaImage, FaVideo, FaMicrophone, FaStar, FaExternalLinkAlt,FaPhone,FaRegStar,FaDownload
} from 'react-icons/fa';

// Extend the moment library for react-big-calendar
const localizer = momentLocalizer(moment);

// Dummy data for projects
const initialProjects = [
  {
    id: 'proj1',
    title: 'AI-Driven Climate Change Analysis',
    description: 'Leveraging machine learning to predict and mitigate climate change effects.',
    members: ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Williams'],
    progress: 65,
    tasks: [
      { id: 'task1', content: 'Data collection', status: 'done', assignee: 'John Doe', dueDate: '2023-06-15' },
      { id: 'task2', content: 'Model development', status: 'in-progress', assignee: 'Jane Smith', dueDate: '2023-07-01' },
      { id: 'task3', content: 'Validation', status: 'to-do', assignee: 'Bob Johnson', dueDate: '2023-07-15' },
      { id: 'task4', content: 'Report writing', status: 'to-do', assignee: 'Alice Williams', dueDate: '2023-07-30' },
    ],
    milestones: [
      { id: 'ms1', title: 'Initial data analysis complete', date: '2023-06-30', status: 'completed' },
      { id: 'ms2', title: 'Model prototype ready', date: '2023-07-15', status: 'upcoming' },
      { id: 'ms3', title: 'Final report submission', date: '2023-08-15', status: 'upcoming' },
    ],
    risksAndIssues: [
      { id: 'risk1', description: 'Data quality concerns', severity: 'high', mitigation: 'Implement rigorous data cleaning processes' },
      { id: 'issue1', description: 'Computational resources shortage', severity: 'medium', resolution: 'Exploring cloud computing options' },
    ]
  },
  // ... (add more projects with similar detailed structure)
];

const CRDSpace = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [projects, setProjects] = useState(initialProjects);
  const [selectedProject, setSelectedProject] = useState(null);
  const [notifications, setNotifications] = useState([
    { id: 1, content: 'New comment on AI-Driven Climate Change Analysis', read: false },
    { id: 2, content: 'Task "Data collection" marked as complete', read: true },
    { id: 3, content: 'Meeting scheduled: Project Review on July 1st', read: false },
  ]);

  useEffect(() => {
    // Simulating real-time updates
    const interval = setInterval(() => {
      setNotifications(prevNotifications => [
        { id: Date.now(), content: `Update ${Math.floor(Math.random() * 100)}`, read: false },
        ...prevNotifications
      ]);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedProject(null);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setActiveTab('project-detail');
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">CRD Space</h1>
          <div className="flex items-center space-x-4">
            <NotificationPanel notifications={notifications} />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="flex border-b">
            <TabButton 
              active={activeTab === 'dashboard'} 
              onClick={() => handleTabChange('dashboard')}
              icon={FaChartLine}
            >
              Dashboard
            </TabButton>
            <TabButton 
              active={activeTab === 'projects'} 
              onClick={() => handleTabChange('projects')}
              icon={FaTasks}
            >
              Projects
            </TabButton>
            <TabButton 
              active={activeTab === 'chat'} 
              onClick={() => handleTabChange('chat')}
              icon={FaComments}
            >
              Team Chat
            </TabButton>
            <TabButton 
              active={activeTab === 'resources'} 
              onClick={() => handleTabChange('resources')}
              icon={FaFile}
            >
              Resources
            </TabButton>
            <TabButton 
              active={activeTab === 'calendar'} 
              onClick={() => handleTabChange('calendar')}
              icon={FaCalendarAlt}
            >
              Calendar
            </TabButton>
            <TabButton 
              active={activeTab === 'members'} 
              onClick={() => handleTabChange('members')}
              icon={FaUsers}
            >
              Team
            </TabButton>
          </div>
          <div className="p-6">
            {activeTab === 'dashboard' && <Dashboard projects={projects} />}
            {activeTab === 'projects' && <ProjectsList projects={projects} onSelectProject={handleProjectSelect} />}
            {activeTab === 'chat' && <EnhancedGroupChat />}
            {activeTab === 'resources' && <EnhancedResourceSpace />}
            {activeTab === 'calendar' && <EnhancedCalendar />}
            {activeTab === 'members' && <EnhancedMembersList />}
            {activeTab === 'project-detail' && selectedProject && (
              <ProjectDetail project={selectedProject} onBack={() => setActiveTab('projects')} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const TabButton = ({ children, active, onClick, icon: Icon }) => (
  <button
    className={`flex items-center px-6 py-4 font-semibold transition-colors duration-200 ${
      active 
        ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
    }`}
    onClick={onClick}
  >
    <Icon className="mr-2" />
    {children}
  </button>
);

const NotificationPanel = ({ notifications }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500"
      >
        <FaBell size={24} />
        {notifications.some(n => !n.read) && (
          <span className="absolute top-0 right-0 block h-3 w-3 rounded-full bg-red-500 ring-2 ring-white"></span>
        )}
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg overflow-hidden z-20">
          <div className="py-2">
            <div className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100">
              Notifications
            </div>
            <div className="divide-y divide-gray-200">
              {notifications.map(notification => (
                <div key={notification.id} className="px-4 py-3 hover:bg-gray-50 transition ease-in-out duration-150">
                  <p className="text-sm text-gray-600">{notification.content}</p>
                  {!notification.read && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      New
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-800 focus:outline-none focus:text-gray-800"
      >
        <img
          className="h-8 w-8 rounded-full"
          src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
          alt=""
        />
        <span className="ml-2">John Doe</span>
        <FaEllipsisV className="ml-2" />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg overflow-hidden z-20">
          <div className="py-1">
            <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Your Profile</a>
            <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Settings</a>
            <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Sign out</a>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ projects }) => {
  const totalTasks = projects.reduce((sum, project) => sum + project.tasks.length, 0);
  const completedTasks = projects.reduce((sum, project) => 
    sum + project.tasks.filter(task => task.status === 'done').length, 0
  );

  const projectProgress = projects.map(project => ({
    name: project.title,
    progress: project.progress
  }));

  const taskDistribution = [
    { status: 'To Do', count: projects.reduce((sum, project) => sum + project.tasks.filter(task => task.status === 'to-do').length, 0) },
    { status: 'In Progress', count: projects.reduce((sum, project) => sum + project.tasks.filter(task => task.status === 'in-progress').length, 0) },
    { status: 'Done', count: completedTasks }
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DashboardCard
          title="Total Projects"
          value={projects.length}
          icon={FaTasks}
          color="bg-blue-500"
        />
        <DashboardCard
          title="Total Tasks"
          value={totalTasks}
          icon={FaCheckCircle}
          color="bg-green-500"
        />
        <DashboardCard
          title="Completed Tasks"
          value={completedTasks}
          icon={FaCheck}
          color="bg-purple-500"
        />
        <DashboardCard
          title="Overall Progress"
          value={`${Math.round((completedTasks / totalTasks) * 100)}%`}
          icon={FaChartLine}
          color="bg-yellow-500"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Project Progress</h3>
          <div className="h-80">
            <Bar
              data={{
                labels: projectProgress.map(p => p.name),
                datasets: [{
                  label: 'Progress (%)',
                  data: projectProgress.map(p => p.progress),
                  backgroundColor: 'rgba(54, 162, 235, 0.6)',
                  borderColor: 'rgba(54, 162, 235, 1)',
                  borderWidth: 1
                }]
              }}
              options={{
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 100
                  }
                }
              }}
            />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Task Distribution</h3>
          <div className="h-80">
            <Doughnut
              data={{
                labels: taskDistribution.map(t => t.status),
                datasets: [{
                  data: taskDistribution.map(t => t.count),
                  backgroundColor: [
                    'rgba(255, 99, 132, 0.6)',
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(75, 192, 192, 0.6)'
                  ],
                  borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(75, 192, 192, 1)'
                  ],
                  borderWidth: 1
                }]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Recent Activities</h3>
        <ul className="space-y-3">
          {[...Array(5)].map((_, index) => (
            <li key={index} className="flex items-center space-x-3 text-sm">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                {['JD', 'JS', 'BJ', 'AW'][index % 4]}
              </span>
              <span className="flex-grow">
                <span className="font-medium">{['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Williams'][index % 4]}</span>
                {' '}
                {['updated task', 'commented on', 'completed', 'started'][index % 4]}
                {' '}
                <span className="font-medium">"{['Data collection', 'Model development', 'Validation', 'Report writing'][index % 4]}"</span>
              </span>
              <span className="flex-shrink-0 text-gray-500">{`${index + 1}h ago`}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DashboardCard = ({ title, value, icon: Icon, color }) => (
  <div className={`${color} text-white rounded-lg shadow-md p-6`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium uppercase">{title}</p>
        <p className="text-3xl font-bold mt-1">{value}</p>
      </div>
      <Icon size={24} />
    </div>
  </div>
);

const ProjectsList = ({ projects, onSelectProject }) => {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Research Projects</h2>
        <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition duration-300 flex items-center">
          <FaPlus className="mr-2" /> New Project
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <ProjectCard key={project.id} project={project} onSelect={onSelectProject} />
        ))}
      </div>
    </div>
  );
};

const ProjectCard = ({ project, onSelect }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300">
    <div className="p-6">
      <h3 className="text-xl font-semibold mb-2 text-gray-900">{project.title}</h3>
      <p className="text-gray-600 mb-4">{project.description}</p>
      <div className="flex items-center justify-between mb-4">
        <div className="flex -space-x-2">
          {project.members.slice(0, 3).map((member, index) => (
            <div
              key={index}
              className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-sm font-semibold"
            >
              {member.charAt(0)}
            </div>
          ))}
          {project.members.length > 3 && (
            <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-sm font-semibold">
              +{project.members.length - 3}
            </div>
          )}
        </div>
        <span className="text-sm font-semibold text-blue-600">{project.progress}% Complete</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${project.progress}%` }}></div>
      </div>
      <button
        onClick={() => onSelect(project)}
        className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition duration-300"
      >
        View Details
      </button>
    </div>
  </div>
);

const ProjectDetail = ({ project, onBack }) => {
  const [tasks, setTasks] = useState(project.tasks);

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const newTasks = Array.from(tasks);
    const [reorderedItem] = newTasks.splice(result.source.index, 1);
    newTasks.splice(result.destination.index, 0, reorderedItem);

    // Update the status based on the new column
    reorderedItem.status = result.destination.droppableId;

    setTasks(newTasks);
  };

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="mb-4 text-blue-600 hover:text-blue-800 font-semibold flex items-center"
      >
        <FaTimes className="mr-2" /> Back to Projects
      </button>
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">{project.title}</h2>
        <p className="text-gray-600 mb-6">{project.description}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-xl font-semibold mb-2">Team Members</h3>
            <ul className="space-y-2">
              {project.members.map((member, index) => (
                <li key={index} className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-semibold mr-2">
                    {member.charAt(0)}
                  </div>
                  {member}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2">Progress</h3>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
              <div
                className="bg-blue-600 h-4 rounded-full"
                style={{ width: `${project.progress}%` }}
              ></div>
            </div>
            <p className="text-sm font-semibold">{project.progress}% Complete</p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mb-4">Kanban Board</h3>
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {['to-do', 'in-progress', 'done'].map((status) => (
              <div key={status} className="bg-gray-100 p-4 rounded-lg">
                <h4 className="text-lg font-semibold mb-2 capitalize">{status.replace('-', ' ')}</h4>
                <Droppable droppableId={status}>
                  {(provided) => (
                    <ul
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {tasks
                        .filter((task) => task.status === status)
                        .map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className="bg-white p-3 rounded shadow-sm"
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium">{task.content}</p>
                                    <p className="text-sm text-gray-500">Assignee: {task.assignee}</p>
                                  </div>
                                  <span className="text-xs font-semibold bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    {task.dueDate}
                                  </span>
                                </div>
                              </li>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>

        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4">Milestones</h3>
          <ul className="space-y-4">
            {project.milestones.map((milestone) => (
              <li key={milestone.id} className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-2 ${
                  milestone.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                <div>
                  <p className="font-medium">{milestone.title}</p>
                  <p className="text-sm text-gray-500">{milestone.date}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4">Risks and Issues</h3>
          <ul className="space-y-4">
            {project.risksAndIssues.map((item) => (
              <li key={item.id} className="bg-white p-4 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded text-sm font-semibold ${
                    item.severity === 'high' ? 'bg-red-100 text-red-800' :
                    item.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {item.severity.toUpperCase()}
                  </span>
                  <FaExclamationCircle className="text-yellow-500" />
                </div>
                <p className="mt-2 font-medium">{item.description}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {item.mitigation || item.resolution}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const EnhancedGroupChat = () => {
  const [messages, setMessages] = useState([
    { id: 1, sender: 'John Doe', content: `Hey team, how's the progress on the climate model?`, timestamp: '10:30 AM', avatar: 'https://i.pravatar.cc/300?img=1' },
    { id: 2, sender: 'Jane Smith', content: `We're making good progress. The initial results look promising!`, timestamp: '10:35 AM', avatar: 'https://i.pravatar.cc/300?img=5' },
    { id: 3, sender: 'Bob Johnson', content: `Great to hear! I've uploaded some new datasets to the resources section.`, timestamp: '10:40 AM', avatar: 'https://i.pravatar.cc/300?img=8' },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim()) {
      setMessages([...messages, {
        id: messages.length + 1,
        sender: 'You',
        content: newMessage,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        avatar: 'https://i.pravatar.cc/300?img=12'
      }]);
      setNewMessage('');
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(newMessage + emoji);
  };

  return (
    <div className="h-[600px] flex flex-col bg-white rounded-lg shadow-md">
      <div className="p-4 border-b flex justify-between items-center">
        <h2 className="text-xl font-semibold">Team Chat</h2>
        <div className="flex space-x-2">
          <button className="text-gray-500 hover:text-gray-700">
            <FaVideo />
          </button>
          <button className="text-gray-500 hover:text-gray-700">
            <FaPhone />
          </button>
        </div>
      </div>
      <div className="flex-grow overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.sender === 'You' ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex max-w-xs lg:max-w-md ${message.sender === 'You' ? 'flex-row-reverse' : ''}`}>
              <img src={message.avatar} alt={message.sender} className="w-8 h-8 rounded-full" />
              <div className={`ml-2 p-3 rounded-lg ${
                message.sender === 'You' ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}>
                <p className="font-semibold text-sm">{message.sender}</p>
                <p>{message.content}</p>
                <p className="text-xs mt-1 text-gray-500">{message.timestamp}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 border-t">
        <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
          <button type="button" onClick={() => setShowEmojiPicker(!showEmojiPicker)} className="text-gray-500hover:text-gray-700">
            <FaSmile />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-grow p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button type="button" className="text-gray-500 hover:text-gray-700">
            <FaPaperclip />
          </button>
          <button
            type="submit"
            className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition duration-300"
          >
            <FaPaperPlane />
          </button>
        </form>
        {showEmojiPicker && (
          <div className="mt-2">
            {/* Simplified emoji picker */}
            <div className="flex flex-wrap">
              {['😊', '😂', '🎉', '👍', '❤️', '🔥', '🤔', '👀'].map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleEmojiSelect(emoji)}
                  className="p-1 text-2xl hover:bg-gray-200 rounded"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EnhancedResourceSpace = () => {
  const [resources, setResources] = useState([
    { id: 1, name: 'Climate Dataset 2023.csv', type: 'csv', size: '2.5 MB', uploadedBy: 'John Doe', date: '2023-05-15', downloads: 12, starred: false },
    { id: 2, name: 'Research Proposal.docx', type: 'docx', size: '568 KB', uploadedBy: 'Jane Smith', date: '2023-05-10', downloads: 8, starred: true },
    { id: 3, name: 'Model Architecture.pdf', type: 'pdf', size: '1.2 MB', uploadedBy: 'Bob Johnson', date: '2023-05-08', downloads: 15, starred: false },
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setResources([...resources, {
        id: resources.length + 1,
        name: file.name,
        type: file.name.split('.').pop(),
        size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        uploadedBy: 'You',
        date: new Date().toISOString().split('T')[0],
        downloads: 0,
        starred: false
      }]);
    }
  };

  const handleStarResource = (id) => {
    setResources(resources.map(resource =>
      resource.id === id ? { ...resource, starred: !resource.starred } : resource
    ));
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
  };

  const sortedResources = [...resources].sort((a, b) => {
    if (a[sortBy] < b[sortBy]) return sortOrder === 'asc' ? -1 : 1;
    if (a[sortBy] > b[sortBy]) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredResources = sortedResources.filter(resource =>
    resource.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Resource Space</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <label htmlFor="file-upload" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition duration-300 cursor-pointer flex items-center">
            <FaPlus className="mr-2" />
            Upload File
          </label>
          <input id="file-upload" type="file" className="hidden" onChange={handleFileUpload} />
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('type')} className="flex items-center">
                  Type
                  {sortBy === 'type' && (sortOrder === 'asc' ? '▲' : '▼')}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('size')} className="flex items-center">
                  Size
                  {sortBy === 'size' && (sortOrder === 'asc' ? '▲' : '▼')}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Uploaded By
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('date')} className="flex items-center">
                  Date
                  {sortBy === 'date' && (sortOrder === 'asc' ? '▲' : '▼')}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button onClick={() => handleSort('downloads')} className="flex items-center">
                  Downloads
                  {sortBy === 'downloads' && (sortOrder === 'asc' ? '▲' : '▼')}
                </button>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredResources.map((resource) => (
              <tr key={resource.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <FaFile className="mr-2 text-gray-400" />
                    <span className="font-medium text-gray-900">{resource.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {resource.type.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resource.size}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resource.uploadedBy}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resource.date}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{resource.downloads}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button onClick={() => handleStarResource(resource.id)} className="text-yellow-400 hover:text-yellow-500 mr-3">
                    {resource.starred ? <FaStar /> : <FaRegStar />}
                  </button>
                  <button className="text-indigo-600 hover:text-indigo-900 mr-3">
                    <FaDownload />
                  </button>
                  <button className="text-red-600 hover:text-red-900">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EnhancedCalendar = () => {
  const [events, setEvents] = useState([
    {
      id: 1,
      title: 'Project Kickoff Meeting',
      start: new Date(2023, 5, 15, 10, 0),
      end: new Date(2023, 5, 15, 11, 0),
    },
    {
      id: 2,
      title: 'Data Analysis Workshop',
      start: new Date(2023, 5, 20, 14, 0),
      end: new Date(2023, 5, 20, 16, 0),
    },
  ]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: new Date(),
    end: new Date(),
  });

  const handleSelectSlot = ({ start, end }) => {
    setNewEvent({ title: '', start, end });
    setShowEventModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCreateEvent = () => {
    if (newEvent.title) {
      setEvents([...events, { ...newEvent, id: events.length + 1 }]);
      setShowEventModal(false);
      setNewEvent({ title: '', start: new Date(), end: new Date() });
    }
  };

  const handleUpdateEvent = () => {
    setEvents(events.map(event => event.id === selectedEvent.id ? selectedEvent : event));
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleDeleteEvent = () => {
    setEvents(events.filter(event => event.id !== selectedEvent.id));
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Calendar</h2>
      <div className="bg-white rounded-lg shadow-md p-6">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 500 }}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          selectable
        />
      </div>

      {showEventModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-10" id="my-modal">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {selectedEvent ? 'Edit Event' : 'Create New Event'}
              </h3>
              <div className="mt-2 px-7 py-3">
                <input
                  type="text"
                  className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none"
                  placeholder="Event Title"
                  value={selectedEvent ? selectedEvent.title : newEvent.title}
                  onChange={(e) => selectedEvent 
                    ? setSelectedEvent({...selectedEvent, title: e.target.value})
                    : setNewEvent({...newEvent, title: e.target.value})
                  }
                />
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700">Start Time</label>
                  <input
                    type="datetime-local"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"
                    value={moment(selectedEvent ? selectedEvent.start : newEvent.start).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      selectedEvent 
                        ? setSelectedEvent({...selectedEvent, start: date})
                        : setNewEvent({...newEvent, start: date});
                    }}
                  />
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700">End Time</label>
                  <input
                    type="datetime-local"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm"
                    value={moment(selectedEvent ? selectedEvent.end : newEvent.end).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      selectedEvent 
                        ? setSelectedEvent({...selectedEvent, end: date})
                        : setNewEvent({...newEvent, end: date});
                    }}
                  />
                </div>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  id="ok-btn"
                  className="px-4 py-2 bg-blue-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  onClick={selectedEvent ? handleUpdateEvent : handleCreateEvent}
                >
                  {selectedEvent ? 'Update Event' : 'Create Event'}
                </button>
                {selectedEvent && (
                  <button
                    className="mt-2 px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
                    onClick={handleDeleteEvent}
                  >
                    Delete Event
                  </button>
                )}
                <button
                  className="mt-2 px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EnhancedMembersList = () => {
  const [members, setMembers] = useState([
    { id: 1, name: 'John Doe', role: 'Lead Researcher', institution: 'University A', email: 'john.doe@univ-a.edu', avatar: 'https://i.pravatar.cc/300?img=1', skills: ['Data Analysis', 'Machine Learning'], projects: ['AI-Driven Climate Change Analysis'] },
    { id: 2, name: 'Jane Smith', role: 'Professor', institution: 'University B', email: 'jane.smith@univ-b.edu', avatar: 'https://i.pravatar.cc/300?img=5', skills: ['Quantum Computing', 'Algorithm Design'], projects: ['Quantum Computing in Cryptography'] },
    { id: 3, name: 'Bob Johnson', role: 'PhD Student', institution: 'University C', email: 'bob.johnson@univ-c.edu', avatar: 'https://i.pravatar.cc/300?img=8', skills: ['Statistical Analysis', 'Data Visualization'], projects: ['AI-Driven Climate Change Analysis'] },
    { id: 4, name: 'Alice Williams', role: 'Industry Partner', institution: 'Tech Corp', email: 'alice.w@techcorp.com', avatar: 'https://i.pravatar.cc/300?img=9', skills: ['Project Management', 'Software Development'], projects: ['Quantum Computing in Cryptography'] },
  ]);

  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: '', institution: '', email: '', skills: '', projects: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  const handleAddMember = (e) => {
    e.preventDefault();
    setMembers([...members, { 
      id: members.length + 1, 
      ...newMember, 
      skills: newMember.skills.split(',').map(skill => skill.trim()),
      projects: newMember.projects.split(',').map(project => project.trim()),
      avatar: `https://i.pravatar.cc/300?img=${Math.floor(Math.random() * 70) + 1}`
    }]);
    setNewMember({ name: '', role: '', institution: '', email: '', skills: '', projects: '' });
    setShowAddMember(false);
  };

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.institution.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <button
            onClick={() => setShowAddMember(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition duration-300 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add Member
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMembers.map((member) => (
          <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <img src={member.avatar} alt={member.name} className="w-16 h-16 rounded-full mr-4" />
                <div>
                  <h3 className="text-xl font-semibold">{member.name}</h3>
                  <p className="text-gray-600">{member.role}</p>
                </div>
              </div>
              <p className="text-gray-600 mb-2"><strong>Institution:</strong> {member.institution}</p>
              <p className="text-gray-600 mb-2"><strong>Email:</strong> {member.email}</p>
              <div className="mb-2">
                <strong>Skills:</strong>
                <div className="flex flex-wrap mt-1">
                  {member.skills.map((skill, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 mb-2 px-2.5 py-0.5 rounded">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <strong>Projects:</strong>
                <ul className="list-disc list-inside mt-1">
                  {member.projects.map((project, index) => (
                    <li key={index} className="text-gray-600">{project}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setSelectedMember(member)}
                  className="text-blue-500 hover:text-blue-700"
                >
                  View Profile
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" id="add-member-modal">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Member</h3>
              <form onSubmit={handleAddMember} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <input
                    type="text"
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Institution</label>
                  <input
                    type="text"
                    value={newMember.institution}
                    onChange={(e) => setNewMember({ ...newMember, institution: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Skills (comma-separated)</label>
                  <input
                    type="text"
                    value={newMember.skills}
                    onChange={(e) => setNewMember({ ...newMember, skills: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Projects (comma-separated)</label>
                  <input
                    type="text"
                    value={newMember.projects}
                    onChange={(e) => setNewMember({ ...newMember, projects: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowAddMember(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
                  >
                    Add Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedMember && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full" id="member-profile-modal">
          <div className="relative top-20 mx-auto p-5 border w-3/4 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start">
                <h3 className="text-2xl font-medium text-gray-900 mb-4">{selectedMember.name}'s Profile</h3>
                <button onClick={() => setSelectedMember(null)} className="text-gray-500 hover:text-gray-700">
                  <FaTimes />
                </button>
              </div>
              <div className="flex items-center mb-6">
                <img src={selectedMember.avatar} alt={selectedMember.name} className="w-24 h-24 rounded-full mr-6" />
                <div>
                  <h4 className="text-xl font-semibold">{selectedMember.name}</h4>
                  <p className="text-gray-600">{selectedMember.role}</p>
                  <p className="text-gray-600">{selectedMember.institution}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h5 className="text-lg font-semibold mb-2">Contact Information</h5>
                  <p><strong>Email:</strong> {selectedMember.email}</p>
                </div>
                <div>
                  <h5 className="text-lg font-semibold mb-2">Skills</h5>
                  <div className="flex flex-wrap">
                    {selectedMember.skills.map((skill, index) => (
                      <span key={index} className="bg-blue-100 text-blue-800 text-xs font-semibold mr-2 mb-2 px-2.5 py-0.5 rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-span-2">
                  <h5 className="text-lg font-semibold mb-2">Projects</h5>
                  <ul className="list-disc list-inside">
                    {selectedMember.projects.map((project, index) => (
                      <li key={index} className="text-gray-600">{project}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRDSpace;